import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { PortalTranslationService } from './portal-translation.service';

export interface PortalCrumb {
  label: string;
  /** Router link; the last crumb (the page itself) has none. */
  link: string | null;
}

/** The path shown after the logo: Case queue, Case queue > CR-100000, Reports, Settings. */
@Injectable({ providedIn: 'root' })
export class PortalBreadcrumbService {
  private readonly router = inject(Router);
  private readonly translation = inject(PortalTranslationService);
  private readonly path = signal(this.router.url);

  /** Name of the open case (its assignment code), set by the case page while it is open. */
  readonly detailLabel = signal<string | null>(null);

  readonly crumbs = computed<PortalCrumb[]>(() => {
    const segments = this.path().split(/[?#]/)[0].split('/').filter(Boolean);
    const [section, id] = segments;
    const queue = this.translation.translate('myAssignments');

    if (section === 'queue' && id) return [{ label: queue, link: '/queue' }, { label: this.detailLabel() ?? '…', link: null }];
    if (section === 'queue') return [{ label: queue, link: null }];
    if (section === 'reports') return [{ label: this.translation.translate('reports'), link: null }];
    if (section === 'settings') return [{ label: this.translation.translate('settings'), link: null }];

    return [];
  });

  constructor() {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.path.set(event.urlAfterRedirects));
  }
}
